/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useState } from 'react';

import { Link, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import { Grid, Hidden, Card } from '@material-ui/core';

import Breadcrumbs from '@navigation/Breadcrumbs';
import { capitalize } from '@helpers/utils';
import * as Routes from '@constants/routes';
import { Divider, ShowDotsMenu } from '@components';
import { Button, Icon } from 'fpay-ui';
import { USERS } from '@constants/routes';

import { useUser } from '@hooks';
import styled from 'styled-components';
import { UserStatus } from '@components/UserStatus';
import { USER_STATUS } from '@constants/user';
import BlockUserModal from '@screen/UserList/BlockUserModal';
import classes from './UserDetail.module.scss';
import { isCountryPeruOrColombia } from 'constants/regions';

const StyledLink = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
  margin-bottom: 16px;
`;

const StyledTitle = styled.span(
  ({ theme }) => `
    ${theme.textTypes['Subtitle-1-w500']}
  `
);

const UserEdit = ({ route }) => {
  const {
    selected,
    resendInvitation,
    unsubscribeUser,
    deleteUser,
    getPermissionActionsByUser,
  } = useUser({
    fetchUsers: false,
  });
  const history = useHistory();
  const { t } = useTranslation();
  const [confirmModal, setConfirmModal] = useState(false);
  const { APPROVED, UNSUBSCRIBE, DELETED } = USER_STATUS;

  const [deleteBlockUser, setDeleteBlockUser] = useState({
    action: '',
    first_name: '',
    last_name: '',
  });

  const handleDeleteBlockClick = (user, action) => {
    setDeleteBlockUser({ ...user, action });
    setConfirmModal(true);
  };

  const {
    canResendInvitation,
    canEdit,
    canDelete,
    canBlock,
    canUnblock,
    canEditRol,
  } = getPermissionActionsByUser(selected);

  const setModalText = () => {
    if (deleteBlockUser.action === UNSUBSCRIBE) {
      return `¿Quiere bloquear a ${capitalize(deleteBlockUser?.first_name)} ${capitalize(
        deleteBlockUser.last_name
      )}?`;
    }
    if (deleteBlockUser.action === DELETED) {
      return `¿Quiere eliminar a ${capitalize(deleteBlockUser?.first_name)} ${capitalize(
        deleteBlockUser.last_name
      )}?`;
    }
    return `¿Quiere desbloquear a ${capitalize(deleteBlockUser?.first_name)} ${capitalize(
      deleteBlockUser.last_name
    )}?`;
  };

  const handleConfirmBlock = () => {
    if (deleteBlockUser.action === DELETED) {
      deleteUser(deleteBlockUser);
      setConfirmModal(false);
      return;
    }
    unsubscribeUser(deleteBlockUser);
    setConfirmModal(false);
  };

  return (
    <>
      <Grid container className={classes.useredit}>
        <Grid item container xs={12} className={classes.titleBar}>
          <Hidden mdDown>
            <Breadcrumbs route={route} />
          </Hidden>
        </Grid>
        <Grid item container xs={12} className={classes.detail}>
          <StyledLink to={USERS} className="justLarge">
            <Icon icon="ic_backarrow" width="20.1px" height="20.1px" color="#43B02A" />
            <Button type="link" data-testid="redir-button" testId="redir-button">
              {t('users.detail.back')}
            </Button>
          </StyledLink>
          <Card className={classes.card}>
            <section className={classes.cardHeader}>
              <StyledTitle>{t('users.detail.title')}</StyledTitle>
              <div className={classes.dotsContainer}>
                {selected.status !== APPROVED && (
                  <UserStatus className={classes.userStatus} data-testid="blocked-rol">
                    {selected.status}
                  </UserStatus>
                )}
                <ShowDotsMenu
                  {...{ canEdit, canBlock, canUnblock, canDelete }}
                  {...{
                    handleEdit: () => history.push(Routes.USER_EDIT),
                    handleBlock: () => handleDeleteBlockClick(selected, UNSUBSCRIBE),
                    handleUnblock: () => handleDeleteBlockClick(selected, APPROVED),
                    handleDelete: () => handleDeleteBlockClick(selected, DELETED),
                  }}
                />
              </div>
            </section>
            <Grid id="user-name" container className={classes.item}>
              <Grid item xs={12} md={2} className={classes.key}>
                <p>{t('users.detail.name')}</p>
              </Grid>
              <Grid container item md={10} xs={12}>
                <Grid item xs={10} className={classes.value}>
                  <p>{capitalize(selected.first_name)}</p>
                </Grid>
              </Grid>
            </Grid>
            <Divider />
            <Grid id="userd-patern-last" container className={classes.item}>
              <Grid item xs={12} md={2} className={classes.key}>
                <p>{t('users.detail.patern_last')}</p>
              </Grid>
              <Grid container item md={10} xs={12}>
                <Grid item xs={10} className={classes.value}>
                  <p>{capitalize(selected.patern_last)}</p>
                </Grid>
              </Grid>
            </Grid>
            <Divider />
            <Grid id="userd-matern-last" container className={classes.item}>
              <Grid item xs={12} md={2} className={classes.key}>
                <p>{t('users.detail.matern_last')}</p>
              </Grid>
              <Grid container item md={10} xs={12}>
                <Grid item xs={10} className={classes.value}>
                  <p>{selected.matern_last.length ? capitalize(selected.matern_last) : '-'}</p>
                </Grid>
              </Grid>
            </Grid>
            <Divider />
            <Grid id="userd-doc-number" container className={classes.item}>
              <Grid item xs={12} md={2} className={classes.key}>
                <p>{t(`users.detail.docType.${selected?.doc_type.toLowerCase()}`)}</p>
              </Grid>
              <Grid container item md={10} xs={12}>
                <Grid item xs={10} className={classes.value}>
                  <p>{selected.doc_number}</p>
                </Grid>
              </Grid>
            </Grid>
            <Divider />
            <Grid id="user-email" container className={classes.item}>
              <Grid item xs={12} md={2} className={classes.key}>
                <p>{t('users.detail.email')}</p>
              </Grid>
              <Grid container item md={10} xs={12}>
                <Grid item xs={10} className={classes.value}>
                  <p>{selected.email.toLowerCase()}</p>
                </Grid>
                {canResendInvitation && (
                  <Grid
                    id="user-resend-invitation"
                    item
                    xs={2}
                    className={classes.action}
                    onClick={() => resendInvitation(selected.email)}
                    data-testid="document-number-testId"
                  >
                    <p>{t('users.actions.resendInvitation')}</p>
                  </Grid>
                )}
              </Grid>
            </Grid>
            <Divider />
            <Grid id="user-phone" container className={classes.item}>
              <Grid item xs={12} md={2} className={classes.key}>
                <p>{t('users.detail.phone')}</p>
              </Grid>
              <Grid container item md={10} xs={12}>
                <Grid item xs={10} className={classes.value}>
                  <p>{selected.phone[selected.phone.length - 1].full_number}</p>
                </Grid>
              </Grid>
            </Grid>
            <Divider />
            <Grid id="user-role" container className={classes.item}>
              <Grid item xs={12} md={2} className={classes.key}>
                <p>{t('users.detail.role')}</p>
              </Grid>
              <Grid container item md={10} xs={12}>
                <Grid item xs={10} className={classes.value}>
                  <p>{t(`roles.${selected.roles[0]}`)}</p>
                </Grid>
                {canEditRol && !isCountryPeruOrColombia && (
                  <Grid
                    id="user-role-edit"
                    item
                    xs={2}
                    className={classes.action}
                    onClick={() => history.push(Routes.EDIT_ROLE)}
                    data-testid="role-testId"
                  >
                    <div className={classes.iconEditContainer}>
                      <Icon icon="ic-edit" height="24px" width="24px" />
                    </div>
                  </Grid>
                )}
              </Grid>
            </Grid>
            <Divider />
          </Card>
        </Grid>
      </Grid>
      {confirmModal && (
        <BlockUserModal
          open={confirmModal}
          data-testid="block-user-modal"
          testId="block-user-modal"
          textBody={setModalText()}
          textConfirm={t('common.accept')}
          confirmAction={handleConfirmBlock}
          toggleModal={() => setConfirmModal(false)}
        />
      )}
    </>
  );
};

UserEdit.propTypes = {
  route: PropTypes.object.isRequired,
};

export default UserEdit;
